import * as Sentry from '@sentry/react';

// A/B backend routing — DISABLED (2026-07-24).
//
// Every company now resolves the backend from the Vercel env vars
// (REACT_APP_REST_API / REACT_APP_GRAPHQL_URL). The per-company override to
// the "sin Prisma" services is commented out below, not deleted, so the A/B
// can be resumed by restoring this file.
//
// Why it was disabled: the two backends share the same MongoDB but NOT the
// same GraphQL schema. The "Backend sin Prisma" service (Railway, branch
// fable-test) drifted behind master, so any field added to master made every
// query from the A/B company fail schema validation — e.g. `messageType` and
// `structuredPayload` returned `Cannot query field "messageType" on type
// "Message"` for that cohort only. The A/B assumed schema parity; it no
// longer holds while fable-test lags master.
//
// Before re-enabling: merge master into the sin-Prisma branch and redeploy it,
// so both backends expose the same schema.
//
// The module and its exported signatures are kept so callers
// (apollo/apolloClient.js, axios.js, iventas_api_v2.js, redux/store.js,
// utils/aiBotsConfig.js, utils/aiBotsMedia.js, utils/fetchFromApi.js) do not
// change.

// Company that was routed to the sin-Prisma backend while the A/B ran
// (Mobieffects iVentas). Kept for reference and to resume the cohort.
export const AB_COMPANY_ID = '5ed824990274390007afcc70';

// --- A/B override, disabled -------------------------------------------------
// Scope was REST + GraphQL HTTP only. WS subscriptions always stayed on the
// env backend (both backends share the same MongoDB, so realtime kept
// working).
//
// const AB_URLS = {
//   rest: 'https://restmongo.iventas.com',
//   graphql: 'https://graphmongo.iventas.com/',
// };
//
// let storeRef = null;
//
// function currentCompanyId() {
//   try {
//     const data = storeRef && storeRef.getState()?.user?.data;
//     return data?.company?.id ?? data?.companyId ?? null;
//   } catch (e) {
//     // Routing must never break a request: unknown state -> default backend.
//     return null;
//   }
// }
// ---------------------------------------------------------------------------

// Kill-switch for non-prod runs (e.g. the local environment, whose Mongo
// dump contains the A/B company): with
// REACT_APP_DISABLE_BACKEND_OVERRIDE="true" every request keeps the
// env-configured backend instead of the hardcoded prod URLs of the AB_URLS
// block above. The var is never set in prod builds, so prod behavior is
// unchanged. Only the exact string 'true' activates it (fail-closed).
// The guard is applied inside BOTH getters below — where the URL is
// decided — so it survives any future restoration of the A/B routing.
let killSwitchWarned = false;
export function isBackendOverrideDisabled() {
  if (process.env.REACT_APP_DISABLE_BACKEND_OVERRIDE !== 'true') return false;
  if (!killSwitchWarned) {
    killSwitchWarned = true;
    // Loud on purpose: if this ever fires in prod, the variable leaked into
    // Vercel and would silently turn off the A/B cohort.
    // eslint-disable-next-line no-console
    console.warn(
      '[backendOverride] A/B routing disabled by REACT_APP_DISABLE_BACKEND_OVERRIDE',
    );
    // Actionable signal (nobody watches prod browser consoles): shows up in
    // Sentry as a warning event. Best-effort — if the first call happens
    // before Sentry.init, the SDK drops it silently, which is fine.
    Sentry.captureMessage('backendOverride.killswitch_active', {
      level: 'warning',
      tags: { component: 'backendOverride' },
    });
  }
  return true;
}

// Kept as a no-op so redux/store.js keeps calling it unchanged. Restore the
// `storeRef` assignment above when the A/B is resumed.
// eslint-disable-next-line no-unused-vars
export function attachBackendOverrideStore(store) {}

// Always false while the A/B is disabled: no company is routed away from the
// env-configured backend.
export function isAbCompany() {
  if (isBackendOverrideDisabled()) return false;
  // While the A/B is disabled, no company is routed away regardless of the
  // flag. Restore `return currentCompanyId() === AB_COMPANY_ID;` here when
  // the A/B resumes.
  return false;
}

export function getRestApiUrl() {
  // The kill-switch lives here (not only in isAbCompany) so restoring the
  // commented ternary below cannot silently drop it.
  if (isBackendOverrideDisabled()) return process.env.REACT_APP_REST_API;
  // return isAbCompany() ? AB_URLS.rest : process.env.REACT_APP_REST_API;
  return process.env.REACT_APP_REST_API;
}

export function getGraphqlUrl() {
  if (isBackendOverrideDisabled()) return process.env.REACT_APP_GRAPHQL_URL;
  // return isAbCompany() ? AB_URLS.graphql : process.env.REACT_APP_GRAPHQL_URL;
  return process.env.REACT_APP_GRAPHQL_URL;
}
